import {
  GridItem,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Stack,
  StackDivider,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { formatAddress } from '@pelicargo/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import { IconLanding } from '../Icons/IconLanding';
import { IconTakeOff } from '../Icons/IconTakeOff';
import { IconTruck } from '../Icons/IconTruck';
import { SimpleField } from '../SimpleField';
import { AirportPicker } from './AirportPicker';
import { ClosestAirportPicker } from './ClosestAirportPicker';
import { SimplePickerField } from './SimplePickerField';

export const DynamicRoutePicker = ({ isExw }: { isExw: boolean }) => {
  const closestAirportSearchRef = useRef<HTMLInputElement>(null);
  const airportSearchRef = useRef<HTMLInputElement>(null);
  const gridItemRef = useRef<HTMLDivElement>(null);
  const { control } = useFormContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isBodyOrigin, setIsBodyOrigin] = useState<boolean>();

  const [originAirports, destinationAirports, cargoOrigin] = useWatch({
    control,
    name: ['origin_airports', 'destination_airports', 'cargo_origin'],
  });
  const { errors } = useFormState({ control, name: ['origin_airports', 'destination_airports'] });

  useOutsideClick({ ref: gridItemRef, handler: onClose });

  const name = useMemo(() => formatAddress(cargoOrigin), [cargoOrigin]);

  const showClosestAirportPicker = useMemo(() => isBodyOrigin && isExw, [isBodyOrigin, isExw]);
  const originLabel = useMemo(() => (isExw ? 'Pickup Address' : 'Origin'), [isExw]);
  const originValue = useMemo(
    () => (isExw && name ? name : originAirports?.length ? originAirports.join(', ') : originLabel),
    [isExw, name, originAirports, originLabel],
  );

  // Manually handle search ref because Popover cannot handle us switching between two different components
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      if (showClosestAirportPicker) {
        closestAirportSearchRef.current?.focus();
      } else {
        airportSearchRef.current?.focus();
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isOpen, showClosestAirportPicker, isBodyOrigin]);
  // isBodyOrigin is needed as a dependency in case the search type changes while the popover is open

  return (
    <GridItem ref={gridItemRef} colSpan={{ base: 1, lg: 6 }} w="full" position="relative" overflow="visible">
      <Popover isOpen={isOpen} matchWidth>
        <PopoverAnchor>
          <SimpleField
            removePadding
            w="full"
            h="full"
            errorMessage={errors.origin_airports?.message || errors.destination_airports?.message}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
            }}
          >
            <Stack
              direction={{ base: 'column', sm: 'row' }}
              divider={<StackDivider display={{ base: 'block', sm: 'none' }} />}
              align="center"
              spacing="4"
              maxW="full"
              w="full"
            >
              <SimplePickerField
                label={originLabel}
                displayValue={originValue}
                isSelected={originAirports?.length}
                icon={isExw ? IconTruck : IconTakeOff}
                onOpen={() => {
                  setIsBodyOrigin(true);
                  onOpen();
                }}
              />
              <SimplePickerField
                label="Destination"
                displayValue={destinationAirports?.length > 0 ? destinationAirports.join(', ') : 'Destination'}
                isSelected={destinationAirports?.length}
                icon={IconLanding}
                onOpen={() => {
                  setIsBodyOrigin(false);
                  onOpen();
                }}
              />
            </Stack>
          </SimpleField>
        </PopoverAnchor>
        <PopoverContent w="full" minW="unset" maxH={430} overflow="auto" boxShadow="2xl">
          <PopoverBody
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
            }}
          >
            <Show if={showClosestAirportPicker}>
              <ClosestAirportPicker
                addressField="cargo_origin"
                onClose={onClose}
                initialFocusRef={closestAirportSearchRef}
              />
            </Show>
            <Show if={!showClosestAirportPicker}>
              <AirportPicker
                isOrigin={isBodyOrigin}
                onClose={onClose}
                maxSelected={4}
                initialFocusRef={airportSearchRef}
              />
            </Show>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </GridItem>
  );
};
